/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import { App, Modal, Alert, Tag } from 'antd'
import { CloseOutlined, WarningOutlined } from '@ant-design/icons'
import useSocialFeed, { Post } from '@sportninja/common/hooks/useSocialFeed'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'
import { TextAreaInput } from '../ts-components/TextAreaInput'
import ImageUploader from '../ts-components/ImageUploader'
import { Organization } from '@sportninja/common/types/Organization'
import { Team } from '@sportninja/common/types/TeamStanding'
import { Schedule } from '@sportninja/common/types/Schedule'
import { TextInput } from '../ts-components/TextInput'
import Button from '../ts-components/Button'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { getSelfUser } from '@sportninja/common/selectors/users'
import { connect } from 'react-redux'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { SwitchInput } from '../ts-components/SwitchInput'
import {
  FeedPostModalIconButtons,
  TargetAudienceSelector,
  AlsoSendViaSection,
  EntityDisplay,
  AudienceBreakdownSection,
  // VisibleToSection,
} from './components'
import req from '@sportninja/common/api/request'

const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(10px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`
const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    padding: '16px',
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backdropFilter: 'blur(10px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
    overflow: 'hidden',
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

/**
 * @interface AudienceBreakdownData
 * @property {number} total_users - Total number of users in the audience
 * @property {number} following_users - Number of users following the entity
 * @property {number} player_users - Number of player users
 * @property {number} official_users - Number of official users
 * @property {number} team_official_users - Number of team official users
 * @property {number} league_admin_users - Number of league admin users
 * @property {number} videographer_users - Number of videographer users
 * @property {number} admin_users - Number of admin users
 */
interface AudienceBreakdownData {
  total_users: number
  following_users: number
  player_users: number
  official_users: number
  team_official_users: number
  league_admin_users: number
  videographer_users: number
  admin_users: number
}

/**
 * Notification Warning Modal component - warns users about sending notifications to audience
 */
const NotificationWarningModal = ({
  isOpen,
  onClose,
  onConfirm,
  sendViaEmail,
  sendViaPush,
  isLoading,
  audienceData,
  isLoadingAudience,
}: {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  sendViaEmail: boolean
  sendViaPush: boolean
  isLoading: boolean
  audienceData: AudienceBreakdownData | null
  isLoadingAudience: boolean
}) => {
  const getNotificationTypes = () => {
    const types: string[] = []
    if (sendViaEmail) {
      types.push('email')
    }
    if (sendViaPush) {
      types.push('push notification')
    }
    return types.join(' and ')
  }

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        margin: 24px;
        padding: 0;
        max-width: 480px;
        .ant-modal-content {
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={onClose}
      centered
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Warning Icon */}
        <WarningOutlined
          css={css`
            font-size: 48px;
            color: ${colors.WARNING_200};
          `}
        />

        {/* Title */}
        <Text
          variant='heading3'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            text-align: center;
          `}
        >
          Share This Update With The Selected Audience?
        </Text>

        {/* Description */}
        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 16px;
            display: flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
            >
              You are about to send{' '}
              <span
                css={css`
                  font-weight: 700;
                `}
              >
                {getNotificationTypes()}
              </span>{' '}
              notifications to all users in your selected audience. This action
              cannot be undone.
            </Text>
          </div>

          {/* Audience Breakdown */}
          <div
            css={css`
              align-self: stretch;
              background: ${colors.SECONDARY_200};
              border-radius: 8px;
              padding: 16px;
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='semiBold'
              color={colors.WHITE}
            >
              Target Audience Breakdown
            </Text>

            {isLoadingAudience ? (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_100}
              >
                Loading audience data...
              </Text>
            ) : audienceData ? (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                `}
              >
                {audienceData.admin_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Admins: {audienceData.admin_users.toLocaleString()} User
                    {audienceData.admin_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.league_admin_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    League Admins:{' '}
                    {audienceData.league_admin_users.toLocaleString()} User
                    {audienceData.league_admin_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.official_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Officials: {audienceData.official_users.toLocaleString()}{' '}
                    User
                    {audienceData.official_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.videographer_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Videographers:{' '}
                    {audienceData.videographer_users.toLocaleString()} User
                    {audienceData.videographer_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.following_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Following: {audienceData.following_users.toLocaleString()}{' '}
                    User
                    {audienceData.following_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.player_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Players: {audienceData.player_users.toLocaleString()} User
                    {audienceData.player_users > 1 ? 's' : ''}
                  </Text>
                )}
                {audienceData.team_official_users > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Team Officials:{' '}
                    {audienceData.team_official_users.toLocaleString()} User
                    {audienceData.team_official_users > 1 ? 's' : ''}
                  </Text>
                )}
                <div
                  css={css`
                    margin-top: 8px;
                    padding-top: 8px;
                    border-top: 1px solid ${colors.SECONDARY_100};
                  `}
                >
                  <Text
                    variant='paragraphSmall'
                    weight='semiBold'
                    color={colors.WHITE}
                  >
                    Total: {audienceData.total_users.toLocaleString()} Unique
                    Users
                  </Text>
                </div>
              </div>
            ) : (
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_100}
              >
                Unable to load audience data
              </Text>
            )}
          </div>

          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
            >
              Are you sure you want to continue?
            </Text>
          </div>
        </div>

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
          `}
        />

        {/* Action Buttons */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: center;
            gap: 12px;
            display: flex;
            flex-direction: column;
          `}
        >
          <Button
            text={isLoading ? 'POSTING...' : 'SEND NOTIFICATIONS'}
            onClick={onConfirm}
            disabled={isLoading}
            isLoading={isLoading}
            variant='primary'
            style={css`
              width: 100%;
            `}
          />
          <Button
            text='CANCEL'
            onClick={onClose}
            disabled={isLoading}
            variant='secondary'
            style={css`
              width: 100%;
            `}
          />
        </div>
      </div>
    </Modal>
  )
}

/**
 * Define the shape of the form state
 */
interface IPostFormState {
  /** Whether the image upload section is visible */
  showImage: boolean
  /** Whether the link input section is visible */
  showLink: boolean
  /** Whether the send via (email/push) section is visible */
  showSendVia: boolean
  /** Whether to send the post via email */
  sendViaEmail: boolean
  /** Whether to send the post via push notification */
  sendViaPush: boolean
  /** Whether users can reply to this post */
  canReply: boolean
  /** Whether to target only active competitions */
  activeCompetition: boolean
  /** Array of role IDs that can see this post */
  visibleToRoles: string[]
  /** The post title */
  title: string
  /** The post content/body */
  content: string
  /** Optional URL link to include in the post */
  url: string
  /** ID of the parent entity (organization, team, or schedule) */
  parentId: string
  /** Type of the parent entity */
  parentEntityType?: 'team' | 'schedule' | 'organization'
  /** Base64 encoded image data or image ID */
  image: string
  /** Whether the image has been changed (for edit mode) */
  imageChanged: boolean
  /** Currently selected entity ID */
  selected?: string
  /** Type of the currently selected entity */
  selectedType: string | null
  /** Number representing the target audience size */
  targetAudience: number | null
  /** Whether the form is currently submitting */
  isLoading: boolean
  /** Current error message, if any */
  error: string | null
  /** Array of field names that have validation errors */
  fieldErrors: string[]
  /** Object mapping field names to their specific error messages */
  customError: { [key: string]: string }
}

/**
 * Define the initial state with default values for all form fields
 */
const initialState: IPostFormState = {
  showImage: false,
  showLink: false,
  showSendVia: true,
  sendViaEmail: false,
  sendViaPush: false,
  canReply: false,
  activeCompetition: false,
  visibleToRoles: [],
  title: '',
  content: '',
  url: '',
  parentId: '',
  parentEntityType: undefined,
  image: '',
  imageChanged: false,
  selected: undefined,
  selectedType: null,
  targetAudience: null,
  isLoading: false,
  error: null,
  fieldErrors: [],
  customError: {},
}

/**
 * Define actions the reducer can handle for state management
 */
type FormAction =
  | { type: 'SET_FIELD'; field: keyof IPostFormState; payload: any }
  | { type: 'RESET_FORM' }
  | { type: 'SET_POST_TO_EDIT'; payload: Post | null }
  | { type: 'TOGGLE_SECTION'; section: 'image' | 'link' | 'sendVia' }
  | {
      type: 'SET_VALIDATION_ERRORS'
      fieldErrors: string[]
      customError: { [key: string]: string }
    }
  | { type: 'CLEAR_VALIDATION_ERRORS' }
  | { type: 'SET_SEND_VIA'; sendType: 'email' | 'push'; value: boolean }

/**
 * Form reducer function to handle state updates in a predictable way
 * @param state - Current form state
 * @param action - Action object describing the state change
 * @returns Updated form state
 */
function formReducer(
  state: IPostFormState,
  action: FormAction
): IPostFormState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.payload }

    case 'RESET_FORM':
      return { ...initialState }

    case 'SET_POST_TO_EDIT': {
      if (!action.payload) {
        return { ...initialState }
      }
      const post = action.payload
      const imageId = getImageThumbnailId(post)
      const postUrl = post.url
      return {
        ...state,
        image: imageId,
        showImage: Boolean(imageId),
        title: post.title,
        content: post.body,
        url: postUrl,
        showLink: Boolean(postUrl),
        parentId: post.parent_id,
        parentEntityType: post.parent_type as
          | 'team'
          | 'schedule'
          | 'organization',
        selected: post.parent_id,
        selectedType: post.parent_type,
        canReply: (post as any).can_reply || false,
        activeCompetition: (post as any).active_competition || false,
        visibleToRoles: (post as any).role_types || [],
      }
    }

    case 'TOGGLE_SECTION':
      switch (action.section) {
        case 'image':
          return { ...state, showImage: !state.showImage }
        case 'link':
          return { ...state, showLink: !state.showLink }
        case 'sendVia':
          return { ...state, showSendVia: !state.showSendVia }
        default:
          return state
      }

    case 'SET_VALIDATION_ERRORS':
      return {
        ...state,
        fieldErrors: action.fieldErrors,
        customError: action.customError,
      }

    case 'CLEAR_VALIDATION_ERRORS':
      return {
        ...state,
        fieldErrors: [],
        customError: {},
      }

    case 'SET_SEND_VIA': {
      const newState = {
        ...state,
        [action.sendType === 'email' ? 'sendViaEmail' : 'sendViaPush']:
          action.value,
      }

      // Clear visible roles if both options are unchecked
      if (action.sendType === 'email' && !action.value && !state.sendViaPush) {
        newState.visibleToRoles = []
      }
      if (action.sendType === 'push' && !action.value && !state.sendViaEmail) {
        newState.visibleToRoles = []
      }

      return newState
    }

    default:
      return state
  }
}

/**
 * Filter organizations/teams/schedules for entities where user has update or post permissions,
 * and attach the respective permission object to each entity for further checks.
 * @param response - API response with data and meta.permissions
 * @returns Filtered array of entities, each with a 'permission' property
 */
const filterForUpdatePermission = (response: any) => {
  const { data, meta } = response
  if (!data || !meta || !meta?.permissions) {
    return []
  }
  const onlyNonSandbox = data.filter(
    (org: any) => !org.name_full?.includes('Sandbox')
  )
  return onlyNonSandbox
    .map((d: any) => ({
      ...d,
      permission: meta.permissions[d?.id] || {},
    }))
    .filter((d: any) => {
      const permission = d.permission
      return permission?.update || permission?.post
    })
}

/**
 * @interface FeedPostCreateModalProps
 * @property {boolean} isOpen - Controls the visibility of the modal
 * @property {() => void} onClose - Callback executed when the modal is requested to close
 * @property {string} entityType - The type of the entity the feed belongs to (e.g., 'organization', 'team', 'schedule')
 * @property {string} entityId - The ID of the entity where the post will be created
 * @property {(page: number) => void} refreshNewFeed - Callback to refresh the parent feed view after post creation/update
 * @property {Post | null} postToEdit - The post data for editing. If null, the modal is in "create" mode
 * @property {(post: Post | null) => void} setPostToEdit - Callback to clear the post being edited
 * @property {any} selfUser - Current user data (should be properly typed in the future)
 */
interface FeedPostCreateModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: string
  entityId: string
  refreshNewFeed: (page: number) => void
  postToEdit: Post | null
  setPostToEdit: (post: Post | null) => void
  selfUser: any
  entity?: any
  canTargetAudience: boolean
}

/**
 * A modal component for creating and editing social feed posts.
 *
 * This component provides a comprehensive form interface for users to:
 * - Create new posts with title, content, images, and links
 * - Edit existing posts
 * - Configure post visibility and permissions
 * - Send posts via email and push notifications
 * - Target specific audiences (organizations, teams, schedules)
 *
 * The component uses useReducer for efficient state management and React.memo
 * for performance optimization to prevent unnecessary re-renders.
 *
 * @param props - Component props as defined in FeedPostCreateModalProps
 * @returns JSX element representing the post creation/editing modal
 */
const FeedPostCreateModal: React.FC<FeedPostCreateModalProps> = React.memo(
  ({
    isOpen,
    onClose,
    entityType,
    entityId,
    refreshNewFeed,
    postToEdit,
    setPostToEdit,
    selfUser,
    entity,
    canTargetAudience = false,
  }) => {
    const [formState, dispatch] = useReducer(formReducer, initialState)
    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false)
    const [isAudienceBreakdownLoading, setIsAudienceBreakdownLoading] =
      useState(false)
    const [audienceData, setAudienceData] =
      useState<AudienceBreakdownData | null>(null)

    // Internal state for organizations, teams, and schedules
    const [orgs, setOrgs] = useState<Organization[]>([])
    const [teams, setTeams] = useState<Team[]>([])
    const [scheds, setScheds] = useState<Schedule[]>([])
    const [isLoadingEntities, setIsLoadingEntities] = useState(false)

    const isSelectedTypeOrg = useMemo(() => {
      return formState.selectedType === 'org'
    }, [formState.selectedType])

    /**
     * Get the currently selected entity with its permissions
     * Similar to getSelectedEntity from TargetAudienceSelector
     */
    const getSelectedEntity = useCallback(() => {
      if (entity) {
        // If we have a direct entity prop, return it (it should have permissions)
        return entity
      }

      if (!formState.selected) {
        return null
      }

      // Search through all entities to find the selected one
      const all = [...orgs, ...teams, ...scheds]
      return all.find((e) => e.id === formState.selected)
    }, [entity, formState.selected, orgs, teams, scheds])

    /**
     * Check if the current user has update or admin permissions for the selected entity
     */
    const hasUpdateOrAdminPermission = useMemo(() => {
      const selectedEntity = getSelectedEntity()
      if (!selectedEntity) {
        return false
      }

      // If entity prop is provided, use the canTargetAudience prop (entity feeds)
      if (entity) {
        return canTargetAudience
      }

      // Check permissions from the permission object attached by filterForUpdatePermission
      const permission = selectedEntity.permission
      return permission?.update || permission?.admin
    }, [getSelectedEntity, entity, canTargetAudience])

    const { message } = App.useApp()
    const isHomePost = entityId === undefined

    const { createPost, updatePost } = useSocialFeed({
      id: entityId || formState.parentId || undefined,
      type: entityType as 'team' | 'schedule' | 'organization' | undefined,
      shouldFetch: false,
    })

    /**
     * Load organizations, teams, and schedules data when modal opens
     * Only loads when there's no entity (i.e., it's not an entity feed)
     */
    const loadEntitiesData = useCallback(async () => {
      if (entity || !isOpen || postToEdit) {
        return
      }

      try {
        setIsLoadingEntities(true)
        const promises = [
          req(
            '/users/me/organizations?per_page=100&sort=name_full&direction=asc'
          ),
          req('/users/me/schedules?per_page=100&sort=starts_at&direction=desc'),
          req('/users/me/teams?per_page=100&sort=name_full&direction=asc'),
        ]

        const [orgResponse, schedResponse, teamsResponse] = await Promise.all(
          promises
        )

        setOrgs(filterForUpdatePermission(orgResponse))
        setScheds(filterForUpdatePermission(schedResponse))
        setTeams(filterForUpdatePermission(teamsResponse))
      } catch (error) {
        console.error('Failed to load entities data:', error)
        message.error('Failed to load organizations, teams, and schedules')
      } finally {
        setIsLoadingEntities(false)
      }
    }, [entity, isOpen, message, postToEdit])

    // Load entities data when modal opens
    useEffect(() => {
      loadEntitiesData()
    }, [loadEntitiesData])

    const handleToggleSection = (section: 'image' | 'link' | 'sendVia') => {
      dispatch({ type: 'TOGGLE_SECTION', section })
    }

    const handleSetSelected = useCallback((value: any) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'selected',
        payload: value,
      })
    }, [])

    const handleSetSelectedType = useCallback((value: string | null) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'selectedType',
        payload: value,
      })
    }, [])

    const handleSetTargetAudience = useCallback((value: number | null) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'targetAudience',
        payload: value,
      })
    }, [])

    const handleSetParentId = useCallback((value: string) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'parentId',
        payload: value,
      })
    }, [])

    const handleSetParentEntityType = useCallback(
      (value: 'team' | 'schedule' | 'organization' | undefined) => {
        dispatch({
          type: 'SET_FIELD',
          field: 'parentEntityType',
          payload: value,
        })
      },
      []
    )

    const handleAudienceBreakdownLoadingChange = useCallback(
      (isLoading: boolean) => {
        setIsAudienceBreakdownLoading(isLoading)
      },
      []
    )

    const handleAudienceDataChange = useCallback(
      (data: AudienceBreakdownData | null) => {
        setAudienceData(data)
      },
      []
    )

    useEffect(() => {
      const orgEmpty = orgs.length === 0
      const teamEmpty = teams.length === 0
      const schedEmpty = scheds.length === 0
      if (!orgEmpty && !teamEmpty && !schedEmpty) {
        return
      }
      if (orgEmpty && !teamEmpty && schedEmpty) {
        dispatch({ type: 'SET_FIELD', field: 'selected', payload: teams[0].id })
        dispatch({ type: 'SET_FIELD', field: 'selectedType', payload: 'team' })
        dispatch({ type: 'SET_FIELD', field: 'parentId', payload: teams[0].id })
        dispatch({
          type: 'SET_FIELD',
          field: 'parentEntityType',
          payload: 'team',
        })
      }
      if (orgEmpty && teamEmpty && !schedEmpty) {
        dispatch({
          type: 'SET_FIELD',
          field: 'selected',
          payload: scheds[0].id,
        })
        dispatch({ type: 'SET_FIELD', field: 'selectedType', payload: 'sched' })
        dispatch({
          type: 'SET_FIELD',
          field: 'parentId',
          payload: scheds[0].id,
        })
        dispatch({
          type: 'SET_FIELD',
          field: 'parentEntityType',
          payload: 'schedule',
        })
      }
      if (!orgEmpty && teamEmpty && schedEmpty) {
        dispatch({ type: 'SET_FIELD', field: 'selected', payload: orgs[0].id })
        dispatch({ type: 'SET_FIELD', field: 'selectedType', payload: 'org' })
        dispatch({ type: 'SET_FIELD', field: 'parentId', payload: orgs[0].id })
        dispatch({
          type: 'SET_FIELD',
          field: 'parentEntityType',
          payload: 'organization',
        })
      }
    }, [orgs, teams, scheds, isOpen])

    useEffect(() => {
      dispatch({ type: 'SET_POST_TO_EDIT', payload: postToEdit })
    }, [postToEdit])

    const handleSubmit = useCallback(async () => {
      dispatch({ type: 'SET_FIELD', field: 'error', payload: null })

      // Validation: check required fields before submit
      const newFieldErrors: string[] = []
      const newCustomError: { [key: string]: string } = {
        ...formState.customError,
      }

      if (formState.title.length === 0) {
        newFieldErrors.push('title')
        newCustomError.title = 'Title is required'
      } else {
        newCustomError.title = ''
      }

      if (formState.content.length === 0) {
        newFieldErrors.push('content')
        newCustomError.content = 'Content is required'
      } else {
        newCustomError.content = ''
      }

      dispatch({
        type: 'SET_VALIDATION_ERRORS',
        fieldErrors: newFieldErrors,
        customError: newCustomError,
      })

      if (newFieldErrors.length > 0) {
        return
      }

      // Check if notifications are enabled and show warning modal
      if (formState.sendViaEmail || formState.sendViaPush) {
        setIsWarningModalOpen(true)
        return
      }

      // Proceed with submission if no notifications
      await performSubmit()
    }, [formState])

    const performSubmit = useCallback(async () => {
      try {
        dispatch({ type: 'SET_FIELD', field: 'isLoading', payload: true })

        let entityTypeToSubmit = formState.parentEntityType || entityType
        if (entityTypeToSubmit === 'sched') {
          entityTypeToSubmit = 'schedule'
        }
        if (entityTypeToSubmit === 'org') {
          entityTypeToSubmit = 'organization'
        }

        if (postToEdit) {
          await updatePost(
            {
              send_email: formState.sendViaEmail ? true : false,
              send_push_notification: formState.sendViaPush ? true : false,
              can_reply: formState.canReply,
              active_competition: formState.activeCompetition,
              title: formState.title,
              body: formState.content,
              image: formState.image,
              role_types: formState.visibleToRoles.map((role) =>
                parseInt(role, 10)
              ),
              url:
                formState.url &&
                !formState.url?.startsWith('http') &&
                formState.url?.length > 0 &&
                formState.url?.includes('.')
                  ? `https://${formState.url}`
                  : formState.url,
            } as any,
            postToEdit.id,
            formState.imageChanged
          )
        } else {
          await createPost(
            {
              send_email: formState.sendViaEmail ? true : false,
              send_push_notification: formState.sendViaPush ? true : false,
              can_reply: formState.canReply,
              active_competition: formState.activeCompetition,
              title: formState.title,
              body: formState.content,
              image: formState.image,
              role_types: formState.visibleToRoles.map((role) =>
                parseInt(role, 10)
              ),
              url:
                formState.url &&
                !formState.url?.startsWith('http') &&
                formState.url?.length > 0 &&
                formState.url?.includes('.')
                  ? `https://${formState.url}`
                  : formState.url,
            } as any,
            entityTypeToSubmit as 'team' | 'schedule' | 'organization'
          )
        }

        message.open({
          type: 'success',
          content: postToEdit
            ? 'Post updated successfully'
            : 'Post created successfully',
        })

        dispatch({ type: 'RESET_FORM' })
        refreshNewFeed(1)
        setPostToEdit(null)
        onClose()
      } catch (e: any) {
        if (e?.invalid_fields) {
          if (e?.invalid_fields?.url) {
            dispatch({
              type: 'SET_VALIDATION_ERRORS',
              fieldErrors: [...formState.fieldErrors, 'website'],
              customError: {
                ...formState.customError,
                website: e?.invalid_fields?.url,
              },
            })
            return
          }
        }
        const errorMessage = getErrorMessage(e)
        dispatch({ type: 'SET_FIELD', field: 'error', payload: errorMessage })
      } finally {
        dispatch({ type: 'SET_FIELD', field: 'isLoading', payload: false })
      }
    }, [
      formState,
      entityType,
      postToEdit,
      message,
      refreshNewFeed,
      setPostToEdit,
      onClose,
      updatePost,
      createPost,
    ])

    const handleWarningModalClose = useCallback(() => {
      setIsWarningModalOpen(false)
    }, [])

    const handleWarningModalConfirm = useCallback(async () => {
      setIsWarningModalOpen(false)
      await performSubmit()
    }, [performSubmit])

    const getHeader = useCallback(() => {
      return (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 16px;
          `}
        >
          <Text
            variant='heading3'
            weight='semiBold'
            color={colors.WHITE}
            textTransform='capitalize'
          >
            {postToEdit ? 'Edit Post' : 'New Post'}
          </Text>
          <Tag
            css={css`
              margin-top: 4px;
            `}
            color={colors.PRIMARY}
          >
            BETA
          </Tag>
        </div>
      )
    }, [postToEdit])

    const closeIcon = useCallback(() => {
      return (
        <CloseOutlined
          css={css`
            color: ${colors.SOLITUDE};
            font-size: 16px;
          `}
        />
      )
    }, [])

    const handleClose = useCallback(() => {
      dispatch({ type: 'RESET_FORM' })
      setPostToEdit(null)
      onClose()
    }, [onClose, setPostToEdit])

    const getFooter = useCallback(() => {
      const shouldDisableSave = isHomePost
        ? !formState.parentEntityType || !formState.parentId
        : !entityType || !entityId

      return (
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            width: 50%;
            margin-left: auto;
          `}
        >
          <Button
            text='Cancel'
            onClick={handleClose}
            variant='secondary'
            disabled={formState.isLoading || isLoadingEntities}
          />
          <Button
            text='Post'
            onClick={handleSubmit}
            disabled={
              formState.isLoading ||
              isLoadingEntities ||
              isAudienceBreakdownLoading ||
              formState.fieldErrors.length > 0 ||
              shouldDisableSave ||
              formState.content.length === 0 ||
              formState.title.length === 0
            }
            isLoading={formState.isLoading}
            variant='primary'
          />
        </div>
      )
    }, [
      formState.content.length,
      formState.fieldErrors.length,
      formState.isLoading,
      formState.parentEntityType,
      formState.parentId,
      formState.title.length,
      entityId,
      entityType,
      handleClose,
      handleSubmit,
      isHomePost,
      isAudienceBreakdownLoading,
      isLoadingEntities,
    ])

    return (
      <Modal
        footer={getFooter()}
        closeIcon={closeIcon()}
        title={getHeader()}
        open={isOpen}
        classNames={classNames}
        styles={modalStyles}
        width={800}
        centered
        onCancel={handleClose}
      >
        <div
          css={css`
            align-self: stretch;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 8px;
            display: flex;
            margin-bottom: 8px;
          `}
        >
          <div
            css={css`
              flex: 1 1 0;
              justify-content: flex-start;
              align-items: center;
              gap: 8px;
              display: flex;
            `}
          >
            <img
              css={css`
                width: 40px;
                height: 40px;
                border-radius: 50%;
                border: 1px white solid;
                object-fit: contain;
              `}
              src={
                selfUser?.imageId ||
                '/images/generic_placeholders/placeholder_player.png'
              }
              alt='User Avatar'
            />
            <div
              css={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
              `}
            >
              <div
                css={css`
                  justify-content: center;
                  align-items: center;
                  gap: 4px;
                  display: flex;
                `}
              >
                <Text
                  variant='paragraphMedium'
                  weight='bold'
                  color={colors.NEUTRAL_0}
                >
                  {selfUser?.fullName}
                </Text>
              </div>
              <div
                css={css`
                  margin-top: 4px;
                  width: 100%;
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Post To:
                </Text>
                {entity ? (
                  <EntityDisplay entity={entity} entityType={entityType} />
                ) : (
                  <TargetAudienceSelector
                    orgs={orgs}
                    teams={teams}
                    scheds={scheds}
                    selected={formState.selected}
                    setSelected={handleSetSelected}
                    selectedType={formState.selectedType}
                    setSelectedType={handleSetSelectedType}
                    targetAudience={formState.targetAudience}
                    setTargetAudience={handleSetTargetAudience}
                    setParentId={handleSetParentId}
                    setParentEntityType={handleSetParentEntityType}
                    disabled={
                      formState.isLoading ||
                      postToEdit !== null ||
                      isLoadingEntities
                    }
                  />
                )}
              </div>
            </div>
          </div>
          <FeedPostModalIconButtons
            showImage={formState.showImage}
            showLink={formState.showLink}
            onToggle={handleToggleSection}
            sendViaSelected={formState.showSendVia}
            onSendViaClick={() => handleToggleSection('sendVia')}
          />
        </div>

        {formState.showSendVia && hasUpdateOrAdminPermission && (
          <div
            css={css`
              align-self: stretch;
              justify-content: flex-start;
              align-items: flex-start;
              gap: 16px;
              display: flex;
              margin-bottom: 16px;
              ${postToEdit &&
              css`
                display: none;
              `}
            `}
          >
            <div
              css={css`
                flex: 1 1 0;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                display: flex;
              `}
            >
              <AlsoSendViaSection
                email={formState.sendViaEmail}
                push={formState.sendViaPush}
                onChange={(type, checked) => {
                  dispatch({
                    type: 'SET_SEND_VIA',
                    sendType: type,
                    value: checked,
                  })
                }}
              />
            </div>

            {/* Vertical Divider */}
            <div
              css={css`
                width: 1px;
                align-self: stretch;
                background: ${colors.SECONDARY_100};
              `}
            />

            <div
              css={css`
                flex: 1 1 0;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
                display: flex;
              `}
            >
              <AudienceBreakdownSection
                selectedEntityId={entity ? entityId : formState.selected}
                selectedEntityType={
                  entity ? entityType : formState.selectedType
                }
                activeCompetition={formState.activeCompetition}
                onLoadingChange={handleAudienceBreakdownLoadingChange}
                onDataChange={handleAudienceDataChange}
              />
            </div>
          </div>
        )}

        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            width: 100%;
            margin-bottom: 16px;
            ${(!isSelectedTypeOrg || !hasUpdateOrAdminPermission) &&
            css`
              display: none;
            `}
          `}
        >
          <SwitchInput
            title='Target Only Active Competitions'
            subtitle='Only send to users in currently active competitions'
            value={formState.activeCompetition}
            onChange={(value) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'activeCompetition',
                payload: value,
              })
            }
          />
        </div>

        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
            flex: 1;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <TextInput
              name='title'
              value={formState.title}
              disabled={formState.isLoading}
              setValue={(value) =>
                dispatch({ type: 'SET_FIELD', field: 'title', payload: value })
              }
              fieldErrors={formState.fieldErrors}
              customError={formState.customError}
              label='Title *'
              placeholder='Add a Title'
            />
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <TextAreaInput
              maxLength={1000}
              name='content'
              value={formState.content}
              setValue={(value) =>
                dispatch({
                  type: 'SET_FIELD',
                  field: 'content',
                  payload: value,
                })
              }
              placeholder='Share Your Update...'
              fieldErrors={formState.fieldErrors}
              customError={formState.customError}
              disabled={formState.isLoading}
              label=''
              showCharacterCount={false}
            />
          </div>
          {formState.showImage && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
              `}
            >
              <ImageUploader
                setImageChanged={(value) =>
                  dispatch({
                    type: 'SET_FIELD',
                    field: 'imageChanged',
                    payload: value,
                  })
                }
                setImageUrl={(value) =>
                  dispatch({
                    type: 'SET_FIELD',
                    field: 'image',
                    payload: value,
                  })
                }
                imageUrl={formState.image}
                label='Add Image'
                tip=''
                messageApi={message}
              />
            </div>
          )}
          {formState.showLink && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
              `}
            >
              <TextInput
                label='Add Link'
                value={formState.url}
                setValue={(value) =>
                  dispatch({ type: 'SET_FIELD', field: 'url', payload: value })
                }
                fieldErrors={formState.fieldErrors}
                customError={formState.customError}
                disabled={false}
                name='website'
                type='url'
                placeholder='https://www.sportninja.com'
              />
            </div>
          )}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              width: 100%;
            `}
          >
            <SwitchInput
              title='Allow Comments'
              subtitle='Let others join the conversation on this post'
              value={formState.canReply}
              onChange={(value) =>
                dispatch({
                  type: 'SET_FIELD',
                  field: 'canReply',
                  payload: value,
                })
              }
            />
          </div>
          {formState.error && (
            <Alert
              message={formState.error}
              type='error'
              css={css`
                margin-top: 16px;
              `}
            />
          )}
        </div>

        {/* Notification Warning Modal */}
        <NotificationWarningModal
          isOpen={isWarningModalOpen}
          onClose={handleWarningModalClose}
          onConfirm={handleWarningModalConfirm}
          sendViaEmail={formState.sendViaEmail}
          sendViaPush={formState.sendViaPush}
          isLoading={formState.isLoading}
          audienceData={audienceData}
          isLoadingAudience={isAudienceBreakdownLoading}
        />
      </Modal>
    )
  }
)

const makeMapStateToProps = () => {
  return (state) => {
    return {
      selfUser: getSelfUser(state),
    }
  }
}
const mapDispatchToProps = (_dispatch) => ({})

export default connect(
  makeMapStateToProps,
  mapDispatchToProps
)(FeedPostCreateModal)
